// src/pages/Sanitary/SanitaryProductsPage.jsx
// =============================================================================
// Sanitary Documentation / Disinfection & Hygiene / Sanitary products
// =============================================================================
//
// Product is still the catalog source of truth. This page maintains only the
// sanitary profile and now separates ordinary profile editing from lifecycle
// actions (activate / deactivate / delete).

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import PaginationFooter from "../../components/common/PaginationFooter";
import {
  deleteSanitaryProductProfile,
  getSanitaryProduct,
  listSanitaryProductsPaged,
  saveSanitaryProductProfile,
  setSanitaryProductProfileActive,
} from "../../services/sanitaryProductService";

import "../Stock/StockMasterDataPage.css";
import "./SanitaryProductsPage.css";

const EMPTY_FORM = {
  sanitary_category: "surfaces",
  form: "ready_to_use",
  requires_dilution: false,
  default_concentration_percent: "",
  working_solution_valid_hours: "",
  requires_batch_tracking: true,
  requires_expiry_tracking: true,
  safety_data_sheet_media_id: "",
  usage_instruction_media_id: "",
  comment: "",
};

const CATEGORIES = ["hands", "surfaces", "tools", "skin", "sanitary", "other"];
const FORMS = ["ready_to_use", "concentrate", "wipes", "powder", "other"];
const PROFILE_STATUSES = ["all", "active", "inactive", "not_configured"];
const PAGE_SIZE_OPTIONS = [25, 50, 100];

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function normalizeForm(row = {}) {
  const profile = row.profile || {};
  return {
    sanitary_category: profile.sanitary_category || "surfaces",
    form: profile.form || "ready_to_use",
    requires_dilution: Boolean(profile.requires_dilution),
    default_concentration_percent: profile.default_concentration_percent ?? "",
    working_solution_valid_hours: profile.working_solution_valid_hours ?? "",
    requires_batch_tracking: profile.requires_batch_tracking !== false,
    requires_expiry_tracking: profile.requires_expiry_tracking !== false,
    safety_data_sheet_media_id: profile.safety_data_sheet_media_id ?? "",
    usage_instruction_media_id: profile.usage_instruction_media_id ?? "",
    comment: profile.comment || "",
  };
}

function formFingerprint(form = {}) {
  return JSON.stringify({
    sanitary_category: String(form.sanitary_category || ""),
    form: String(form.form || ""),
    requires_dilution: Boolean(form.requires_dilution),
    default_concentration_percent: String(form.default_concentration_percent ?? ""),
    working_solution_valid_hours: String(form.working_solution_valid_hours ?? ""),
    requires_batch_tracking: Boolean(form.requires_batch_tracking),
    requires_expiry_tracking: Boolean(form.requires_expiry_tracking),
    safety_data_sheet_media_id: String(form.safety_data_sheet_media_id ?? ""),
    usage_instruction_media_id: String(form.usage_instruction_media_id ?? ""),
    comment: String(form.comment || ""),
  });
}

function formatProductLabel(item) {
  const p = item?.product || {};
  const parts = [p.name_public, p.variant_label, p.sku ? `SKU ${p.sku}` : null].filter(Boolean);
  return parts.join(" · ");
}

function formatPackage(product) {
  if (!product) return "—";
  const label = product.packaging_label || "";
  const qty = product.content_quantity ?? "";
  const unit = product.content_unit_symbol || product.content_unit_code || "";
  const content = qty && unit ? `${qty} ${unit}` : qty || unit || "";

  if (label && content) return `${label} · ${content}`;
  return label || content || "—";
}

function asPayload(form) {
  const requiresDilution = Boolean(form.requires_dilution);
  return {
    sanitary_category: form.sanitary_category,
    form: form.form,
    requires_dilution: requiresDilution,
    default_concentration_percent:
      requiresDilution && form.default_concentration_percent !== ""
        ? form.default_concentration_percent
        : null,
    working_solution_valid_hours:
      requiresDilution && form.working_solution_valid_hours !== ""
        ? form.working_solution_valid_hours
        : null,
    requires_batch_tracking: Boolean(form.requires_batch_tracking),
    requires_expiry_tracking: Boolean(form.requires_expiry_tracking),
    safety_data_sheet_media_id: form.safety_data_sheet_media_id === "" ? null : form.safety_data_sheet_media_id,
    usage_instruction_media_id: form.usage_instruction_media_id === "" ? null : form.usage_instruction_media_id,
    comment: form.comment || null,
  };
}

function dependencyRows(capabilities = {}) {
  const deps = capabilities.dependencies || {};
  return [
    ["activeDirectRules", Number(deps.active_direct_rules || 0)],
    ["activeWorkingSolutionRules", Number(deps.active_working_solution_rules || 0)],
    ["activeSolutionDefinitions", Number(deps.active_solution_definitions || 0)],
  ].filter(([, count]) => count > 0);
}

function stockPolicyBlockerText(capabilities = {}, t) {
  const code = capabilities?.stock_policy?.code || "";
  const keyByCode = {
    SANITARY_BATCH_REQUIRED_FOR_EXPIRY: "stockPolicyBatchForExpiry",
    SANITARY_PRODUCT_STOCK_POLICY_BATCH_REQUIRED: "stockPolicyBatchRequired",
    SANITARY_PRODUCT_STOCK_POLICY_RECEIPT_BATCH_REQUIRED: "stockPolicyReceiptBatchRequired",
    SANITARY_PRODUCT_STOCK_POLICY_EXPIRY_REQUIRED: "stockPolicyExpiryRequired",
  };
  const key = keyByCode[code] || "stockPolicyInvalid";
  return t(`sanitary:products.lifecycle.${key}`);
}

export default function SanitaryProductsPage() {
  const { t } = useTranslation(["sanitary", "common"]);
  const { authState } = useContext(AuthContext);
  const role = normalizeRole(authState?.user?.role);
  const canEdit = role === "superuser" || role === "reception";

  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [baselineForm, setBaselineForm] = useState(EMPTY_FORM);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [profileStatus, setProfileStatus] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [formFilter, setFormFilter] = useState("");
  const [includeInactiveProducts, setIncludeInactiveProducts] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, page_count: 1 });
  const [summary, setSummary] = useState({ total: 0, profiled: 0, active_profiles: 0 });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const selectedProduct = selected?.product || null;
  const isNewProfile = Boolean(selectedProduct && !selected?.profile);
  const dirty = Boolean(
    selectedProduct &&
    canEdit &&
    formFingerprint(form) !== formFingerprint(baselineForm)
  );

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSanitaryProductsPaged({
        q: search,
        profile_status: profileStatus,
        sanitary_category: categoryFilter,
        form: formFilter,
        product_active: includeInactiveProducts ? "" : "true",
        page,
        page_size: pageSize,
      });
      setRows(data.items || []);
      setPagination(data.pagination || { page: 1, page_size: pageSize, total: 0, page_count: 1 });
      setSummary(data.summary || { total: 0, profiled: 0, active_profiles: 0 });
      if (Number(data.pagination?.page || page) !== page) {
        setPage(Number(data.pagination?.page || 1));
      }
    } catch (err) {
      setError(err?.message || t("sanitary:products.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, formFilter, includeInactiveProducts, page, pageSize, profileStatus, search, t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function setEditorRow(row) {
    const nextForm = normalizeForm(row);
    setSelected(row);
    setForm(nextForm);
    setBaselineForm(nextForm);
    setMessage(null);
    setError(null);
  }

  function clearEditor() {
    setSelected(null);
    setForm(EMPTY_FORM);
    setBaselineForm(EMPTY_FORM);
  }

  function confirmDiscard() {
    if (!dirty) return true;
    return window.confirm(t("sanitary:products.messages.discardConfirm"));
  }

  function selectRow(row) {
    if (selectedProduct && String(selectedProduct.id) === String(row?.product?.id)) return;
    if (!confirmDiscard()) return;
    setEditorRow(row);
  }

  function applyContextChange(change) {
    if (!confirmDiscard()) return false;
    clearEditor();
    setMessage(null);
    setError(null);
    change();
    return true;
  }

  function handleSearchSubmit(event) {
    event.preventDefault();
    applyContextChange(() => {
      setPage(1);
      setSearch(searchInput.trim());
    });
  }

  function handleProfileStatusChange(value) {
    applyContextChange(() => {
      setProfileStatus(value);
      if (value === "not_configured") {
        setCategoryFilter("");
        setFormFilter("");
      }
      setPage(1);
    });
  }

  async function refreshAll() {
    if (!confirmDiscard()) return;
    const productId = selectedProduct?.id || null;
    setMessage(null);
    setError(null);
    if (productId) {
      try {
        setLoading(true);
        const [detail] = await Promise.all([
          getSanitaryProduct(productId),
          loadList(),
        ]);
        setEditorRow(detail);
      } catch (err) {
        setError(err?.message || t("sanitary:products.messages.loadFailed"));
      } finally {
        setLoading(false);
      }
    } else {
      await loadList();
    }
  }

  function matchesCurrentProfileFilters(row) {
    const profile = row?.profile || null;
    if (profileStatus === "active" && (!profile || profile.is_active === false)) return false;
    if (profileStatus === "inactive" && (!profile || profile.is_active !== false)) return false;
    if (profileStatus === "not_configured" && profile) return false;
    if (categoryFilter && profile?.sanitary_category !== categoryFilter) return false;
    if (formFilter && profile?.form !== formFilter) return false;
    return true;
  }

  async function refreshSelected(productId, successMessage = null) {
    const [detail] = await Promise.all([
      getSanitaryProduct(productId),
      loadList(),
    ]);
    if (matchesCurrentProfileFilters(detail)) setEditorRow(detail);
    else clearEditor();
    if (successMessage) setMessage(successMessage);
  }

  async function handleSave(event) {
    event.preventDefault();
    if (!selectedProduct || !canEdit) return;

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      await saveSanitaryProductProfile(selectedProduct.id, asPayload(form));
      await refreshSelected(selectedProduct.id, t("sanitary:products.messages.saved"));
    } catch (err) {
      setError(err?.message || t("sanitary:products.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    clearEditor();
    setMessage(null);
    setError(null);
  }

  async function handleToggleActive(targetActive) {
    if (!selectedProduct || !selected?.profile || !canEdit) return;
    if (dirty && !window.confirm(t("sanitary:products.messages.discardBeforeLifecycle"))) return;
    if (!targetActive && !window.confirm(t("sanitary:products.messages.deactivateConfirm", { name: selectedProduct.name_public }))) return;

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await setSanitaryProductProfileActive(selectedProduct.id, targetActive);
      await refreshSelected(selectedProduct.id, t("sanitary:products.messages.statusUpdated"));
    } catch (err) {
      setError(err?.message || t("sanitary:products.messages.statusFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteProfile() {
    if (!selectedProduct || !selected?.profile || !canEdit) return;
    if (dirty && !window.confirm(t("sanitary:products.messages.discardBeforeLifecycle"))) return;
    const confirmed = window.confirm(
      t("sanitary:products.messages.deleteConfirm", { name: selectedProduct.name_public })
    );
    if (!confirmed) return;

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await deleteSanitaryProductProfile(selectedProduct.id);
      clearEditor();
      await loadList();
      setMessage(t("sanitary:products.messages.deleted"));
    } catch (err) {
      setError(err?.message || t("sanitary:products.messages.deleteFailed"));
    } finally {
      setSaving(false);
    }
  }

  const lifecycle = selected?.capabilities || {};
  const activeDependencyRows = useMemo(() => dependencyRows(lifecycle), [lifecycle]);
  const canSave = Boolean(canEdit && selectedProduct && !saving && (dirty || isNewProfile));

  return (
    <main className="stock-md-page">
      <header className="stock-md-header">
        <div>
          <p className="stock-md-eyebrow">{t("sanitary:products.eyebrow")}</p>
          <h1 className="stock-md-title">{t("sanitary:products.title")}</h1>
          <p className="stock-md-subtitle">{t("sanitary:products.subtitle")}</p>
        </div>
        <button type="button" className="stock-md-button" onClick={refreshAll} disabled={loading || saving}>
          {t("common:actions.refresh")}
        </button>
      </header>

      <section className="stock-md-info-box">
        {t("sanitary:products.guidance")}
      </section>

      {error && <div className="stock-md-error">{error}</div>}
      {message && <div className="stock-md-success">{message}</div>}

      <section className="stock-md-card sanitary-products-filter-card">
        <form className="sanitary-products-filter-grid" onSubmit={handleSearchSubmit}>
          <label className="stock-md-field sanitary-products-filter-search">
            <span>{t("sanitary:products.filters.search")}</span>
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t("sanitary:products.filters.searchPlaceholder")}
            />
          </label>

          <label className="stock-md-field">
            <span>{t("sanitary:products.filters.profileStatus")}</span>
            <select value={profileStatus} onChange={(event) => handleProfileStatusChange(event.target.value)}>
              {PROFILE_STATUSES.map((value) => (
                <option key={value} value={value}>{t(`sanitary:products.filters.profileStatuses.${value}`)}</option>
              ))}
            </select>
          </label>

          <label className="stock-md-field">
            <span>{t("sanitary:products.filters.category")}</span>
            <select
              value={categoryFilter}
              disabled={profileStatus === "not_configured"}
              onChange={(event) => applyContextChange(() => { setCategoryFilter(event.target.value); setPage(1); })}
            >
              <option value="">{t("sanitary:products.filters.all")}</option>
              {CATEGORIES.map((value) => (
                <option key={value} value={value}>{t(`sanitary:categories.${value}`)}</option>
              ))}
            </select>
          </label>

          <label className="stock-md-field">
            <span>{t("sanitary:products.filters.form")}</span>
            <select
              value={formFilter}
              disabled={profileStatus === "not_configured"}
              onChange={(event) => applyContextChange(() => { setFormFilter(event.target.value); setPage(1); })}
            >
              <option value="">{t("sanitary:products.filters.all")}</option>
              {FORMS.map((value) => (
                <option key={value} value={value}>{t(`sanitary:forms.${value}`)}</option>
              ))}
            </select>
          </label>

          <label className="stock-md-checkbox sanitary-products-inactive-products">
            <input
              type="checkbox"
              checked={includeInactiveProducts}
              onChange={(event) => applyContextChange(() => { setIncludeInactiveProducts(event.target.checked); setPage(1); })}
            />
            <span>{t("sanitary:products.filters.includeInactiveProducts")}</span>
          </label>

          <button type="submit" className="stock-md-button stock-md-button--primary" disabled={loading}>
            {t("common:actions.search")}
          </button>
        </form>

        <div className="stock-md-stats">
          <span>{t("sanitary:products.stats.total", { count: summary.total })}</span>
          <span>{t("sanitary:products.stats.profiled", { count: summary.profiled })}</span>
          <span>{t("sanitary:products.stats.active", { count: summary.active_profiles })}</span>
        </div>
      </section>

      <div className="stock-md-grid stock-md-grid--wide sanitary-products-main-grid">
        <section className="stock-md-card">
          <h2>{t("sanitary:products.listTitle")}</h2>

          <div className="stock-md-table-wrap">
            <table className="stock-md-table sanitary-products-table">
              <thead>
                <tr>
                  <th>{t("sanitary:products.table.product")}</th>
                  <th>{t("sanitary:products.table.package")}</th>
                  <th>{t("sanitary:products.table.stockUnit")}</th>
                  <th>{t("sanitary:products.table.category")}</th>
                  <th>{t("sanitary:products.table.form")}</th>
                  <th>{t("sanitary:products.table.status")}</th>
                  <th>{t("sanitary:products.table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const product = row.product || {};
                  const profile = row.profile;
                  const selectedRow = selectedProduct && String(selectedProduct.id) === String(product.id);
                  return (
                    <tr key={product.id} className={selectedRow ? "stock-md-row-selected" : ""}>
                      <td>
                        <strong>{product.name_public}</strong>
                        <div className="stock-md-muted">
                          {[product.brand, product.variant_label, product.sku].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </td>
                      <td>{formatPackage(product)}</td>
                      <td>{product.stock_unit_symbol || product.stock_unit_code}</td>
                      <td>{profile ? t(`sanitary:categories.${profile.sanitary_category}`) : "—"}</td>
                      <td>{profile ? t(`sanitary:forms.${profile.form}`) : "—"}</td>
                      <td>
                        {profile ? (
                          <span className={profile.is_active === false ? "stock-md-pill stock-md-pill--muted" : "stock-md-pill"}>
                            {profile.is_active === false
                              ? t("sanitary:products.status.inactive")
                              : t("sanitary:products.status.active")}
                          </span>
                        ) : (
                          <span className="stock-md-pill stock-md-pill--muted">
                            {t("sanitary:products.status.notConfigured")}
                          </span>
                        )}
                      </td>
                      <td>
                        <button type="button" className="stock-md-button stock-md-button--small" onClick={() => selectRow(row)}>
                          {!canEdit
                            ? t("sanitary:products.actions.view")
                            : profile
                              ? t("common:actions.edit")
                              : t("sanitary:products.actions.configure")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr>
                    <td colSpan="7" className="stock-md-empty">
                      {loading ? t("common:common.loading", { defaultValue: "Loading..." }) : t("sanitary:products.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <PaginationFooter
            className="sanitary-products-pagination"
            page={pagination.page}
            pageCount={pagination.page_count}
            pageSize={pagination.page_size}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            totalRows={pagination.total}
            disabled={loading}
            onPageSizeChange={(value) => applyContextChange(() => { setPageSize(value); setPage(1); })}
            onPrev={() => applyContextChange(() => setPage((current) => Math.max(1, current - 1)))}
            onNext={() => applyContextChange(() => setPage((current) => Math.min(pagination.page_count, current + 1)))}
          />
        </section>

        <section className="stock-md-card sanitary-profile-editor-card">
          <h2>{t("sanitary:products.form.title")}</h2>
          {selectedProduct ? (
            <form className="stock-md-form" onSubmit={handleSave}>
              <div className="stock-md-selected-box">
                <strong>{formatProductLabel(selected)}</strong>
                <span>{formatPackage(selectedProduct)} · {selectedProduct.stock_unit_symbol || selectedProduct.stock_unit_code}</span>
              </div>

              {selected?.profile && (
                <div className="sanitary-profile-lifecycle">
                  <div className="sanitary-profile-lifecycle__status">
                    <span className="sanitary-profile-lifecycle__label">{t("sanitary:products.lifecycle.title")}</span>
                    <span className={selected.profile.is_active === false ? "stock-md-pill stock-md-pill--muted" : "stock-md-pill"}>
                      {selected.profile.is_active === false
                        ? t("sanitary:products.status.inactive")
                        : t("sanitary:products.status.active")}
                    </span>
                  </div>

                  {canEdit && (
                    <div className="sanitary-profile-lifecycle__actions">
                      {selected.profile.is_active !== false ? (
                        <button
                          type="button"
                          className="stock-md-button stock-md-button--secondary"
                          onClick={() => handleToggleActive(false)}
                          disabled={saving || !lifecycle.can_deactivate}
                        >
                          {t("sanitary:products.actions.deactivate")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="stock-md-button stock-md-button--secondary"
                          onClick={() => handleToggleActive(true)}
                          disabled={saving || !lifecycle.can_activate}
                        >
                          {t("sanitary:products.actions.activate")}
                        </button>
                      )}

                      {lifecycle.can_delete && (
                        <button
                          type="button"
                          className="stock-md-button stock-md-button--danger-soft"
                          onClick={handleDeleteProfile}
                          disabled={saving}
                        >
                          {t("sanitary:products.actions.deleteProfile")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selected?.profile?.is_active !== false && activeDependencyRows.length > 0 && (
                <div className="sanitary-profile-lifecycle-note sanitary-profile-lifecycle-note--warning">
                  <strong>{t("sanitary:products.lifecycle.deactivationBlocked")}</strong>
                  <ul>
                    {activeDependencyRows.map(([key, count]) => (
                      <li key={key}>{t(`sanitary:products.lifecycle.dependencies.${key}`, { value: count })}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selected?.profile?.is_active === false && !lifecycle.can_activate && (
                <div className="sanitary-profile-lifecycle-note sanitary-profile-lifecycle-note--warning">
                  <strong>{t("sanitary:products.lifecycle.activationBlocked")}</strong>
                  {selectedProduct.is_active === false ? (
                    <span>{t("sanitary:products.lifecycle.productInactive")}</span>
                  ) : lifecycle.stock_policy?.valid === false ? (
                    <span>{stockPolicyBlockerText(lifecycle, t)}</span>
                  ) : null}
                </div>
              )}

              {selected?.profile && !lifecycle.can_delete && lifecycle.has_history_or_references && canEdit && (
                <div className="sanitary-profile-lifecycle-note">
                  {t("sanitary:products.lifecycle.deleteUnavailable")}
                </div>
              )}

              <label className="stock-md-field">
                <span>{t("sanitary:products.form.category")}</span>
                <select
                  value={form.sanitary_category}
                  onChange={(event) => updateField("sanitary_category", event.target.value)}
                  disabled={!canEdit || saving}
                >
                  {CATEGORIES.map((value) => (
                    <option key={value} value={value}>{t(`sanitary:categories.${value}`)}</option>
                  ))}
                </select>
              </label>

              <label className="stock-md-field">
                <span>{t("sanitary:products.form.form")}</span>
                <select
                  value={form.form}
                  onChange={(event) => {
                    const value = event.target.value;
                    setForm((prev) => ({
                      ...prev,
                      form: value,
                      ...(value === "concentrate" ? { requires_dilution: true } : {}),
                    }));
                  }}
                  disabled={!canEdit || saving}
                >
                  {FORMS.map((value) => (
                    <option key={value} value={value}>{t(`sanitary:forms.${value}`)}</option>
                  ))}
                </select>
              </label>

              <label className="stock-md-checkbox sanitary-profile-dilution-toggle">
                <input
                  type="checkbox"
                  checked={form.requires_dilution}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setForm((prev) => ({
                      ...prev,
                      requires_dilution: checked,
                      ...(!checked ? {
                        default_concentration_percent: "",
                        working_solution_valid_hours: "",
                      } : {}),
                    }));
                  }}
                  disabled={!canEdit || saving}
                />
                <span>{t("sanitary:products.form.requiresDilution")}</span>
              </label>

              {form.requires_dilution && (
                <div className="sanitary-profile-solution-section">
                  <div className="sanitary-profile-solution-section__header">
                    <strong>{t("sanitary:products.form.solutionSectionTitle")}</strong>
                    <span>{t("sanitary:products.form.solutionSectionHelp")}</span>
                  </div>
                  <div className="stock-md-two-columns">
                    <label className="stock-md-field">
                      <span>{t("sanitary:products.form.defaultConcentration")}</span>
                      <input
                        type="number"
                        step="0.0001"
                        min="0.0001"
                        max="99.9999"
                        value={form.default_concentration_percent}
                        onChange={(event) => updateField("default_concentration_percent", event.target.value)}
                        disabled={!canEdit || saving}
                      />
                      <small>{t("sanitary:products.form.defaultConcentrationHelp")}</small>
                    </label>

                    <label className="stock-md-field">
                      <span>{t("sanitary:products.form.solutionValidHours")}</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={form.working_solution_valid_hours}
                        onChange={(event) => updateField("working_solution_valid_hours", event.target.value)}
                        disabled={!canEdit || saving}
                      />
                    </label>
                  </div>
                </div>
              )}

              <div className="sanitary-profile-tracking-section">
                <label className="stock-md-checkbox">
                  <input
                    type="checkbox"
                    checked={form.requires_batch_tracking}
                    onChange={(event) => updateField("requires_batch_tracking", event.target.checked)}
                    disabled={!canEdit || saving}
                  />
                  <span>{t("sanitary:products.form.requiresBatchTracking")}</span>
                </label>

                <label className="stock-md-checkbox">
                  <input
                    type="checkbox"
                    checked={form.requires_expiry_tracking}
                    onChange={(event) => updateField("requires_expiry_tracking", event.target.checked)}
                    disabled={!canEdit || saving}
                  />
                  <span>{t("sanitary:products.form.requiresExpiryTracking")}</span>
                </label>
              </div>

              <label className="stock-md-field">
                <span>{t("sanitary:products.form.comment")}</span>
                <textarea
                  rows="4"
                  value={form.comment}
                  onChange={(event) => updateField("comment", event.target.value)}
                  disabled={!canEdit || saving}
                />
              </label>

              {!canEdit && (
                <div className="stock-md-info-box">
                  {t("sanitary:products.messages.readOnly")}
                </div>
              )}

              <div className="stock-md-actions sanitary-profile-editor-actions">
                <button type="button" className="stock-md-button stock-md-button--secondary" onClick={handleCancel} disabled={saving}>
                  {t("common:actions.cancel")}
                </button>
                {canEdit && (
                  <button type="submit" className="stock-md-button stock-md-button--primary" disabled={!canSave}>
                    {saving ? t("sanitary:products.messages.saving") : t("common:actions.save")}
                  </button>
                )}
              </div>
            </form>
          ) : (
            <p className="stock-md-muted">{t("sanitary:products.form.selectProduct")}</p>
          )}
        </section>
      </div>
    </main>
  );
}
